"use client";

import React, { useRef, useEffect, ReactNode } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import classNames from "classnames";

gsap.registerPlugin(ScrollTrigger);

const ANIMATION_DISTANCE = 100;
const ANIMATION_DURATION = 0.8;
const VIEWPORT_THRESHOLD = 0.2;

interface AnimatedContentProps {
  children: ReactNode;
  className?: string;
}

const AnimatedContent: React.FC<AnimatedContentProps> = ({
  children,
  className,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    gsap.set(el, {
      y: ANIMATION_DISTANCE,
      opacity: 0,
    });

    let hasAnimated = false;

    const checkAndAnimate = () => {
      if (hasAnimated) return;

      const rect = el.getBoundingClientRect();
      const isInViewport =
        rect.top < window.innerHeight * (1 - VIEWPORT_THRESHOLD) &&
        rect.bottom > 0;

      if (isInViewport) {
        hasAnimated = true;
        gsap.to(el, {
          y: 0,
          opacity: 1,
          duration: ANIMATION_DURATION,
          ease: "power2.out",
        });
      }
    };

    checkAndAnimate();

    ScrollTrigger.addEventListener("refresh", checkAndAnimate);

    const intervalId = setInterval(checkAndAnimate, 100);

    return () => {
      clearInterval(intervalId);
      ScrollTrigger.removeEventListener("refresh", checkAndAnimate);
      gsap.killTweensOf(el);
    };
  }, []);

  return (
    <div
      className={classNames(className, "overflow-y-visible! pt-4 z-3")}
      ref={ref}
    >
      {children}
    </div>
  );
};

export default AnimatedContent;
